import { z } from 'zod';

// A shopper finding their own order without an account: the number on their confirmation and the phone
// they ordered with, both or nothing. What comes back is what the order status page shows — how far the
// order has come, what is in it and where its parcel is — and never the address, the email or the notes.

/** What a shopper types to find their order. */
export const OrderLookupInputSchema = z.object({
  orderNumber: z.string().trim().min(1).max(50),
  phone: z.string().trim().min(6).max(30)
}).strict();

/** How far an order has come, in the steps a shopper reads it in. */
export const ORDER_STAGES = ['placed', 'shipped', 'delivered', 'cancelled'] as const;
export const OrderStageEnum = z.enum(ORDER_STAGES);

/** One line of a found order. */
export const TrackedOrderItemSchema = z.object({
  title: z.string(),
  variantTitle: z.string().nullable(),
  quantity: z.number().int(),
  imageUrl: z.string().nullable(),
  totalPrice: z.number()
});

/** The parcel's carrier and tracking number, once the order has shipped with one. */
export const OrderTrackingSchema = z.object({
  company: z.string().nullable(),
  number: z.string().nullable(),
  url: z.string().nullable()
});

/** A found order, as its shopper is shown it. */
export const TrackedOrderSchema = z.object({
  orderNumber: z.string(),
  placedAt: z.string(),
  stage: OrderStageEnum,
  city: z.string(),
  currency: z.string(),
  subtotal: z.number(),
  shipping: z.number(),
  discount: z.number(),
  total: z.number(),
  items: z.array(TrackedOrderItemSchema),
  tracking: OrderTrackingSchema.nullable()
});

export type OrderLookupInput = z.infer<typeof OrderLookupInputSchema>;
export type OrderStage = z.infer<typeof OrderStageEnum>;
export type TrackedOrderItem = z.infer<typeof TrackedOrderItemSchema>;
export type OrderTracking = z.infer<typeof OrderTrackingSchema>;
export type TrackedOrder = z.infer<typeof TrackedOrderSchema>;
